import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventoryItemRecord,
  InventoryStockBalanceFilterParams,
  InventoryStockBalanceRecord,
  InventoryStockBalanceSummaryCounts,
  InventoryStockLocationRecord,
  SaveInventoryStockBalanceRequest,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const mapInventoryStockBalance = (
  value: Record<string, unknown>,
): InventoryStockBalanceRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  outlet_id: String(value.outlet_id || ''),
  stock_location_id: String(value.stock_location_id || ''),
  inventory_item_id: String(value.inventory_item_id || ''),
  quantity_on_hand: toNumber(value.quantity_on_hand),
  average_cost: toNumber(value.average_cost),
  total_value: toNumber(value.total_value),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const applyStockBalanceBaseFilters = <T>(
  query: T,
  filters: {
    stockLocationId?: InventoryStockBalanceFilterParams['stockLocationId'];
    inventoryItemIds?: InventoryStockBalanceFilterParams['inventoryItemIds'];
  },
) => {
  let nextQuery = query as any;

  if (filters.stockLocationId && filters.stockLocationId !== 'all') {
    nextQuery = nextQuery.eq('stock_location_id', filters.stockLocationId);
  }

  if (filters.inventoryItemIds?.length) {
    nextQuery = nextQuery.in('inventory_item_id', filters.inventoryItemIds);
  }

  return nextQuery as T;
};

const buildSearchLookup = (
  items: InventoryItemRecord[],
  locations: InventoryStockLocationRecord[],
  search?: string,
) => {
  const trimmedSearch = search?.trim().toLowerCase();
  if (!trimmedSearch) {
    return null;
  }

  const matchingItemIds = new Set(
    items
      .filter((item) =>
        [item.name, item.sku ?? '', item.description ?? '']
          .join(' ')
          .toLowerCase()
          .includes(trimmedSearch),
      )
      .map((item) => item.id),
  );

  const matchingLocationIds = new Set(
    locations
      .filter((location) =>
        [location.name, location.type, location.description ?? '']
          .join(' ')
          .toLowerCase()
          .includes(trimmedSearch),
      )
      .map((location) => location.id),
  );

  return { matchingItemIds, matchingLocationIds };
};

export const fetchInventoryStockBalances = async ({
  companyId,
  outletId,
  search,
  stockLocationId = 'all',
  inventoryItemIds = [],
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventoryStockBalanceFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventoryStockBalanceRecord>>
> => {
  try {
    const [itemsResult, locationsResult] = await Promise.all([
      supabase
        .from('inventory_items')
        .select('*')
        .eq('company_id', companyId)
        .order('name', { ascending: true }),
      supabase
        .from('inventory_stock_locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('outlet_id', outletId)
        .order('name', { ascending: true }),
    ]);

    if (itemsResult.error || locationsResult.error) {
      return {
        success: false,
        error: itemsResult.error?.message || locationsResult.error?.message,
        data: undefined,
      };
    }

    const items = (itemsResult.data || []) as InventoryItemRecord[];
    const locations = (locationsResult.data ||
      []) as InventoryStockLocationRecord[];
    const searchLookup = buildSearchLookup(items, locations, search);

    let query = supabase
      .from('inventory_stock_balances')
      .select('*')
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .order('updated_at', { ascending: false });

    query = applyStockBalanceBaseFilters(query, {
      stockLocationId,
      inventoryItemIds,
    });

    if (searchLookup) {
      const itemIds = Array.from(searchLookup.matchingItemIds);
      const locationIds = Array.from(searchLookup.matchingLocationIds);

      if (!itemIds.length && !locationIds.length) {
        return {
          success: true,
          data: {
            data: [],
            pagination: {
              page,
              pageSize,
              totalItems: 0,
              totalPages: 1,
              hasNextPage: false,
              hasPreviousPage: false,
            },
          },
        };
      }

      if (itemIds.length && locationIds.length) {
        query = query.or(
          `inventory_item_id.in.(${itemIds.join(',')}),stock_location_id.in.(${locationIds.join(',')})`,
        );
      } else if (itemIds.length) {
        query = query.in('inventory_item_id', itemIds);
      } else if (locationIds.length) {
        query = query.in('stock_location_id', locationIds);
      }
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const allRows = (data || []).map((row) =>
      mapInventoryStockBalance(row as Record<string, unknown>),
    );
    const totalItems = allRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const from = (page - 1) * pageSize;
    const pagedRows = allRows.slice(from, from + pageSize);

    return {
      success: true,
      data: {
        data: pagedRows,
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock balances',
      data: undefined,
    };
  }
};

export const fetchInventoryStockBalanceSummary = async (
  companyId: number,
  outletId: string,
  search?: string,
  stockLocationId?: InventoryStockBalanceFilterParams['stockLocationId'],
  inventoryItemIds?: InventoryStockBalanceFilterParams['inventoryItemIds'],
): Promise<ApiResponse<InventoryStockBalanceSummaryCounts>> => {
  try {
    const normalizedStockLocationId = stockLocationId ?? 'all';
    const normalizedInventoryItemIds = inventoryItemIds ?? [];

    const [balancesResult, itemsResult, locationsResult] = await Promise.all([
      fetchInventoryStockBalances({
        companyId,
        outletId,
        search,
        stockLocationId: normalizedStockLocationId,
        inventoryItemIds: normalizedInventoryItemIds,
        page: 1,
        pageSize: 5000,
      }),
      supabase.from('inventory_items').select('*').eq('company_id', companyId),
      supabase
        .from('inventory_stock_locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('outlet_id', outletId),
    ]);

    if (!balancesResult.success || itemsResult.error || locationsResult.error) {
      return {
        success: false,
        error:
          balancesResult.error ||
          itemsResult.error?.message ||
          locationsResult.error?.message,
        data: undefined,
      };
    }

    const balances = balancesResult.data?.data ?? [];
    const itemsMap = new Map(
      ((itemsResult.data || []) as InventoryItemRecord[]).map((item) => [
        item.id,
        item,
      ]),
    );

    const lowStockItems = balances.filter((balance) => {
      const item = itemsMap.get(balance.inventory_item_id);
      return item ? balance.quantity_on_hand <= item.reorder_level : false;
    }).length;

    return {
      success: true,
      data: {
        totalBalanceRows: balances.length,
        totalQuantityOnHand: balances.reduce(
          (total, balance) => total + balance.quantity_on_hand,
          0,
        ),
        totalStockValue: balances.reduce(
          (total, balance) => total + balance.total_value,
          0,
        ),
        lowStockItems,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock balance summary',
      data: undefined,
    };
  }
};

export const fetchInventoryStockBalanceExportRows = async (
  params: Omit<InventoryStockBalanceFilterParams, 'page' | 'pageSize'>,
): Promise<ApiResponse<InventoryStockBalanceRecord[]>> => {
  try {
    const rows: InventoryStockBalanceRecord[] = [];
    let page = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      // eslint-disable-next-line no-await-in-loop
      const result = await fetchInventoryStockBalances({
        ...params,
        page,
        pageSize: 1000,
      });

      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch stock balance export rows',
          data: undefined,
        };
      }

      rows.push(...result.data.data);
      hasNextPage = result.data.pagination.hasNextPage;
      page += 1;

      if (!result.data.data.length) {
        break;
      }
    }

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch stock balance export rows',
      data: undefined,
    };
  }
};

export const saveInventoryStockBalance = async (
  input: SaveInventoryStockBalanceRequest,
): Promise<ApiResponse<InventoryStockBalanceRecord>> => {
  const quantityOnHand = input.quantityOnHand ?? 0;
  const averageCost = input.averageCost ?? 0;
  const payload = {
    company_id: input.companyId,
    outlet_id: input.outletId,
    stock_location_id: input.stockLocationId,
    inventory_item_id: input.inventoryItemId,
    quantity_on_hand: quantityOnHand,
    average_cost: averageCost,
    total_value: quantityOnHand * averageCost,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('inventory_stock_balances')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('outlet_id', input.outletId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapInventoryStockBalance(data) };
    }

    const { data, error } = await supabase
      .from('inventory_stock_balances')
      .upsert(payload, {
        onConflict: 'company_id,outlet_id,stock_location_id,inventory_item_id',
      })
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventoryStockBalance(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save inventory stock balance',
      data: undefined,
    };
  }
};

export const deleteInventoryStockBalance = async (
  companyId: number,
  outletId: string,
  balanceId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('inventory_stock_balances')
      .delete()
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .eq('id', balanceId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete inventory stock balance',
      data: null,
    };
  }
};
